import { useCallback, useMemo, useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_CONFIG } from "@/lib/queryConfig";
import { ProfileNamesContext } from "@/contexts/ProfileNamesContext";

interface ProfileDisplayName {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  /** True when the user holds the 'client' role (resolved definer-side —
   *  user_roles is not readable cross-user from the frontend). */
  is_client?: boolean;
  /** The user's primary role (client > admin > sales > dsa > ops >
   *  licensing_ops > designer > intern). */
  role?: string | null;
}

type ProfileNamesMap = Record<string, ProfileDisplayName>;

/**
 * Hook to fetch profile display names for a list of user IDs.
 * 
 * When used inside Dashboard (with ProfileNamesProvider), this registers
 * the IDs with the shared context for batched fetching. When used outside,
 * it falls back to a standalone query.
 */
export const useProfileDisplayNames = (userIds: (string | null | undefined)[]) => {
  /** Try to use context if available (inside Dashboard) */
  const profileContext = useContext(ProfileNamesContext);
  
  /** Context is available if provider exists - check for registerUserIds function */
  const hasContext = profileContext !== undefined && 
                     profileContext !== null && 
                     typeof profileContext.registerUserIds === 'function';

  /** Filter and dedupe IDs for stable query key */
  const uniqueIds = useMemo(() => {
    return [...new Set(userIds.filter((id): id is string => !!id))].sort();
  }, [userIds]);

  /** Register IDs with context if available - always run this effect */
  useEffect(() => {
    if (hasContext && uniqueIds.length > 0) {
      profileContext.registerUserIds(uniqueIds);
    }
  }, [hasContext, profileContext, uniqueIds]);

  /**
   * Fallback query for when used outside of ProfileNamesProvider
   * This ONLY runs if context is NOT available
   */
  const { data: standaloneNames = {}, isLoading: standaloneLoading, refetch } = useQuery({
    queryKey: ['profile-display-names-standalone', uniqueIds],
    queryFn: async () => {
      if (uniqueIds.length === 0) {
        return {} as ProfileNamesMap;
      }

      const { data, error } = await supabase.rpc('get_profiles_display_names', {
        _user_ids: uniqueIds
      });

      if (error) {
        /**
         * THROW — never return {} as a "successful" result. profiles use a
         * STATIC cache, so a swallowed transient failure (e.g. PostgREST
         * schema-cache reload after a migration) would freeze every name as
         * "Unknown" for the whole session instead of retrying.
         */
        console.error('Error fetching profile display names:', error);
        throw error;
      }

      const namesMap: ProfileNamesMap = {};
      (data || []).forEach((profile: ProfileDisplayName) => {
        namesMap[profile.id] = profile;
      });
      return namesMap;
    },
    /** CRITICAL: Only enable when NO context is available */
    enabled: !hasContext && uniqueIds.length > 0,
    ...QUERY_CONFIG.profiles,
  });

  /** Use context values if available, otherwise use standalone query */
  const contextNames = useMemo(
    () => profileContext.profileNames || {},
    [profileContext.profileNames],
  );
  const profileNames = useMemo(
    () => (hasContext ? contextNames : standaloneNames),
    [hasContext, contextNames, standaloneNames],
  );
  const loading = hasContext ? profileContext.loading : standaloneLoading;

  const getDisplayName = useCallback((userId: string | null | undefined): string => {
    if (!userId) return "Unknown";
    const profile = profileNames[userId];
    return profile ? profile.full_name : "Unknown";
  }, [profileNames]);

  /** Whether the user holds the 'client' role (false until rows load). */
  const getIsClient = useCallback((userId: string | null | undefined): boolean => {
    if (!userId) return false;
    return profileNames[userId]?.is_client === true;
  }, [profileNames]);

  /** The user's primary role name (null until rows load). */
  const getRole = useCallback((userId: string | null | undefined): string | null => {
    if (!userId) return null;
    return profileNames[userId]?.role ?? null;
  }, [profileNames]);

  return { profileNames, loading, getDisplayName, getIsClient, getRole, refetch };
};

/**
 * Utility function to fetch display names for given user IDs (non-hook version)
 */
export const fetchProfileDisplayNames = async (userIds: (string | null | undefined)[]): Promise<ProfileNamesMap> => {
  const uniqueIds = [...new Set(userIds.filter((id): id is string => !!id))];
  
  if (uniqueIds.length === 0) {
    return {};
  }

  try {
    const { data, error } = await supabase.rpc('get_profiles_display_names', {
      _user_ids: uniqueIds
    });

    if (error) {
      console.error('Error fetching profile display names:', error);
      return {};
    }

    const namesMap: ProfileNamesMap = {};
    (data || []).forEach((profile: ProfileDisplayName) => {
      namesMap[profile.id] = profile;
    });
    return namesMap;
  } catch (error) {
    console.error('Error fetching profile display names:', error);
    return {};
  }
};
